import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import styles from "./ProAccounts.module.css";
import cn from 'classnames';
import {Image} from 'react-bootstrap';
import SidebarLayout from '../../../component/common/Layout/SidebarLayout/SidebarLayout';
import Calender from '../../../component/common/Calender/Calender';
import CustomButton from '../../../component/common/CustomButton/CustomButton';
import FilterSelect from '../../../component/common/FilterSelect/FilterSelect';
import SearchInput from '../../../component/common/SearchInput/SearchInput';
import TextHeader from '../../../component/common/TextHeader/TextHeader';
import { IDifferenceType } from '../../../component/common/OverviewCard/OverviewCard';
import ProAccountsCard from '../../../component/common/ProAccountsCard/ProAccountsCard';
import SubscriptionOverviewTable from './component/SubscriptionOverviewTable/SubscriptionOverviewTable';
import { ASSESTS } from '../../../constant/assest';
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchProAccountAnalytics, downloadProAccountList } from '../../../redux/slices/proAccounts/actions';
import { useDebounce } from '../../../hooks/useDebounce';
import { endOfYear, startOfYear } from 'date-fns';
import { toast } from 'react-toastify';
import ErrorService from '../../../services/errorService';

import ProAccountsCardSkeleton from '../../../component/common/ProAccountsCard/ProAccountsCardSkeleton';

const typeOptions = [
    { value: "", label: "All Types" },
    { value: "4", label: "Free" },
    { value: "1", label: "Monthly" },
    { value: "2", label: "Yearly" },
    { value: "5", label: "Lifetime" },
];

const statusOptions = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "cancelled", label: "Cancelled" },
    { value: "blocked", label: "Blocked" },
];

const ProAccounts: FunctionComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { analytics, loading } = useSelector((state: RootState) => state.proAccounts);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([startOfYear(new Date()), endOfYear(new Date())]);
    const [activeFilter, setActiveFilter] = useState<string | null>('currentYear');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);

    const handleTypeSelect = useCallback((value: string) => {
        setSelectedType(value);
    }, []);

    const handleStatusSelect = useCallback((value: string) => {
        setSelectedStatus(value);
    }, []);

    const debouncedSearchTerm = useDebounce(searchTerm.trim(), 500);

    useEffect(() => {
        dispatch(fetchProAccountAnalytics());
    }, [dispatch]);

    const handleDateChange = useCallback((range: [Date | null, Date | null], filter: string | null) => {
        setDateRange(range);
        setActiveFilter(filter);
    }, []);

    const handleResetFilters = useCallback(() => {
        setSearchTerm('');
        setSelectedType('');
        setSelectedStatus('');
        setDateRange([startOfYear(new Date()), endOfYear(new Date())]);
        setActiveFilter('currentYear');
    }, []);

    const formatDate = useCallback((date: Date) => {        
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
    }, []);

    const formattedStartDate = useMemo(() => dateRange[0] ? formatDate(dateRange[0]) : undefined, [dateRange, formatDate]);
    const formattedEndDate = useMemo(() => dateRange[1] ? formatDate(dateRange[1]) : undefined, [dateRange, formatDate]);

    const handleDownload = useCallback(async () => {
        const params: any = {
            search: debouncedSearchTerm || undefined,
            status: selectedStatus || undefined,
            subsType: selectedType || undefined,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
        };

        setIsDownloading(true);
        try {
            const blob = await dispatch(downloadProAccountList(params)).unwrap();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'subscriptions.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(ErrorService.getErrorMessage(error));
        } finally {
            setIsDownloading(false);
        }
    }, [dispatch, debouncedSearchTerm, selectedStatus, selectedType, formattedStartDate, formattedEndDate]);

    const overView = useMemo(() => analytics ? [
        {title: 'Active Subscriptions', value: analytics.total_active_subscription_count?.toString(), differenceType: analytics.active_subscription_growth >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${analytics.active_subscription_growth}% from last month`, actualDifference: analytics.active_subscription_growth},
        {title: 'Inactive Subscriptions', value: analytics.total_inactive_subscription_count?.toString(), differenceType: analytics.inactive_subscription_growth >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${analytics.inactive_subscription_growth}% from last month`, actualDifference: analytics.inactive_subscription_growth},
        {title: 'Churned Users', value: analytics.churned_current_month.toString(), differenceType: analytics.churn_growth >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${analytics.churn_growth}% from last month`, actualDifference: analytics.churn_growth},
        {title: 'Blocked Users', value: analytics.blocked_users_count.toString()},
    ] : [], [analytics]);
 
    return (
        <SidebarLayout>
            <div className={cn(styles.proAccountsTopRow)}>
                <TextHeader 
                    title={'Fishinity Pro Accounts'} 
                    summary={"Monitor and manage all user subscriptions and billing"}
                    className='mb-4'
                />
                <div className={styles.proAccountsCardsRow}>
                    {loading === 'pending' ? (                     
                        Array.from({length: 4}).map((_, index)=> (
                            <ProAccountsCardSkeleton key={index} className={styles.proAccountsOverViewCard} />
                        ))
                    ) : (
                        overView.map((item, index) => (
                            <ProAccountsCard 
                                key={`${item.title}-${index}`}
                                className={styles.proAccountsOverViewCard} 
                                title={item.title} 
                                value={item.value}
                                differenceType={item.differenceType} 
                                differenceText={item.differenceText} 
                                actualDifference={item.actualDifference}
                            />
                        ))
                    )}
                </div>
            </div>
            <div className={cn(styles.dasboardBottomRow)}>
                <TextHeader 
                    title={'Subscription Overview'} 
                    className='mb-2'
                />
                <div className={styles.tableFilter}>
                    <SearchInput 
                        placeholder='Search subscriptions...' 
                        className={styles.proAccountsSearch}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className={cn(styles.tableFilter, styles.tableFilterCols)}>
                        <div className={styles.selectFilter}>
                            <FilterSelect 
                                options={typeOptions} 
                                onSelect={handleTypeSelect} 
                                placeholder={'All Types'} 
                                value={selectedType}
                            />
                        </div>
                        <div className={styles.selectFilter}>
                            <FilterSelect 
                                options={statusOptions} 
                                onSelect={handleStatusSelect} 
                                placeholder={'All Status'} 
                                value={selectedStatus}
                            />
                        </div>
                        <div className={cn(styles.selectFilter, styles.calenderFilter)}>
                            <Calender onDateChange={handleDateChange} dateRange={dateRange} activeFilter={activeFilter} />
                        </div>
                        <div className={styles.deselectFilter}>
                            <CustomButton className={styles.deselectBtn} onClick={handleResetFilters}>
                                Reset All
                            </CustomButton>
                        </div>
                    </div>
                    <div className={styles.downloadBtnWrap}>
                        <CustomButton className={styles.downloadBtn} onClick={handleDownload} disabled={isDownloading}>
                            {isDownloading ? 'Downloading...' : 'Download'} <Image src={ASSESTS.DOWNLOADBTN} alt={'Download'} />
                        </CustomButton>
                    </div>
                </div>
                <SubscriptionOverviewTable 
                    searchTerm={debouncedSearchTerm}
                    status={selectedStatus}
                    subsType={selectedType}
                    formattedStartDate={formattedStartDate}
                    formattedEndDate={formattedEndDate}
                />
            </div>
            <Outlet />
        </SidebarLayout>
    );
};

export default ProAccounts;